// typescript adaption of trie https://github.com/chriswheeldon/trie.ts

class TsTrieNode<T> {
  values: T[];
  children: Map<string, TsTrieNode<T> | undefined>;
  terminal: boolean;

  constructor() {
    this.values = [];
    this.children = new Map();
    this.terminal = false;
  }
}

export class TsTrie<T> {
  private root: TsTrieNode<T>;
  private elements: number;

  constructor() {
    this.root = new TsTrieNode();
    this.elements = 0;
  }

  length() {
    return this.elements;
  }

  // returns all values attached to the node
  // returns null if node does not exist
  // returns empty array if node exists but has no values attached
  getAll(key: string): T[] | null {
    const node: TsTrieNode<T> | null = this.getNode(key);
    return node?.values ?? null;
  }

  // returns true if node exists with or without values attached
  containsNode(key: string): boolean {
    const node: TsTrieNode<T> | null = this.getNode(key);
    return !!node;
  }

  // returns true if node exists and node has values attached
  containsValue(key: string): boolean {
    const node: TsTrieNode<T> | null = this.getNode(key);
    return !!node?.values.length;
  }

  /**
   * takes a key value pair and inserts (adds) the value at the node
   * this allows multiple (potentially identical) values to be stored at each node
   * @param key the trie node
   * @param value the value to be stored at the node
   * @returns
   */
  insert(key: string, value: T): void {
    if (!key || !value) return; // if key or value is empty then return

    const node: TsTrieNode<T> = this.getOrCreateNode(key);

    // the input-path has now been addded to the trie
    if (node && !node.terminal) {
      node.terminal = true; // this node is a terminal node and does/will contain words/suggestions
      this.elements += 1; // count the number of terminal nodes
    }

    if (node && node.terminal) {
      // if this node should contain words/suggestions
      node.values.push(value);
    }
  }

  /**
   * takes a key value pair and updates (replaces) (all)the value(s) if the key already exists
   * otherwise inserts value at the node
   * @param key the trie node
   * @param value the value to be stored (and potentially overwrite existing data) at the node
   * @returns
   */
  upsert(key: string, value: T): void {
    if (!key || !value) return; // if key or value is empty then return

    const node: TsTrieNode<T> = this.getOrCreateNode(key);

    // the input-path has now been addded to the trie
    if (node && !node.terminal) {
      node.terminal = true; // this node is a terminal node and does/will contain words/suggestions
      this.elements += 1; // count the number of terminal nodes
    }

    if (node && node.terminal) {
      // if this node should contain words/suggestions
      node.values = [value];
    }
  }

  // I don't follow what this does - it's not used in this project anyway
  remove(key: string): void {
    const node: TsTrieNode<T> | null = this.getNode(key);
    if (node) {
      node.terminal = false;
      this.elements -= 1;
    }
  }

  /**
   * takes a string and returns the node for the given key or null if the node does not exist
   * @param key
   * @returns the node for the given key or null if the node does not exist
   */
  getNode(key: string): TsTrieNode<T> | null {
    let node: TsTrieNode<T> | null | undefined = this.root;
    let remaining: string = key;
    while (node && remaining.length > 0) {
      let child: TsTrieNode<T> | null | undefined = null;
      for (let i = 1; i <= remaining.length; i += 1) {
        child = node.children.get(remaining.slice(0, i));
        if (child) {
          remaining = remaining.slice(i);
          break;
        }
      }
      node = child;
    }
    return remaining.length === 0 && node ? node : null;
  }

  /**
   * takes a string and creates the node if it does not already exist, returns the node for the given key
   * @param key the trie node
   * @returns the node for the given key
   */
  getOrCreateNode(key: string): TsTrieNode<T> {
    let node: TsTrieNode<T> = this.root;
    let remaining: string = key;
    // for each charater in the string being inserted
    while (remaining.length > 0) {
      let child: TsTrieNode<T> | null | undefined = null; // clear the child node

      // look for a matching child
      for (const [key] of node.children) {
        // for each child of the current node
        if (key === remaining[0]) {
          // if the child matches the first charater in the string being inserted
          child = node.children.get(key); // we have found the correct child node
          break; // no need to check any more children - exit the for-loop
        }
      }

      // if there is no matching child then make one
      if (!child) {
        // if the child node is still blank then there isn't a child which matches the first charater in the string being inserted
        child = new TsTrieNode(); // create a new child node
        node.children.set(remaining[0], child); // set the child node to the first charater in the string being inserted
      }

      // move to the child node and next input character
      node = child; // move to the child node which matches the first charater in the string being inserted
      remaining = remaining.slice(1); // remove the first charater from the string being inserted
    }
    return node;
  }

  commonPrefix(a: string, b: string): string {
    const shortest: number = Math.min(a.length, b.length);
    let i = 0;
    for (; i < shortest; i += 1) {
      if (a[i] !== b[i]) {
        break;
      }
    }
    return a.slice(0, i);
  }
}
